import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { Icon, jnjChevronDown, jnjChevronUp } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { BreakPoint, useMediaQuery } from "@jmc/solid-design-system/src/hooks/useMediaQuery/useMediaQuery";
import { mdiChevronDown, mdiClose, mdiCircle, mdiFormatListBulleted } from "@mdi/js";
import classnames from "classnames";
import React, { Dispatch, MutableRefObject, SetStateAction, useEffect, useRef, useState } from "react";
import { useScroll } from "@jmc/utils/hooks/useScroll";
import { getScreenPercentage } from "@jmc/utils/utils/get-screen-percentage";
import { useHeaderProperties } from "@jmc/core/src/hooks/useHeaderProperties/useHeaderProperties";

import style from "./style.module.scss";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";

/*
 * Arguments to build the table of contents.
 *
 * @param {sectionData[]} contents
 *   All sections to be shown in the table with sectionData format.
 *
 * @param {string} title
 *   Tranlated literal for the component visible title.
 *
 * @param {number} topPosition
 *   Used to calculate the position of the ToC
 *
 * @param {number} topMargin
 *   This parameter will account for the header space to better calculate the visible space of each section.
 *
 * @param {element} scrollArea
 *   The element on which the scrolling of the page takes place.
 */
type PropTypes = {
    sections: sectionData[];
    title: string;
    topBorder?: boolean;
    topPosition?: string;
    topMargin?: number;
    scrollArea?: Document | Element;
    openStatus?: Dispatch<SetStateAction<boolean>>;
    closeLabel?: string;
    openLabel?: string;
};

type ItemPropTypes = {
    id?: string;
    index?: number;
    children: string | JSX.Element | JSX.Element[];
};

export type sectionData = {
    title: string | JSX.Element | JSX.Element[];
    id: string;
    link?: JSX.Element;
    original_title?: string;
    audienceIds?: string;
    shouldBeHiddenInToC?: boolean;
};

const SCROLL_DELAY = 50;
const TOLERANCE = 10;
type chevronState = null | "down" | "up";

const Item = (props: ItemPropTypes): JSX.Element => {
    const { jnjFullBranded } = useJnjBranding();
    const { id, index, children } = props;

    const icon = <Icon className={style.icon} icon={mdiCircle} size="xxs" color="inherit" />;

    return (
        <>
            {jnjFullBranded && icon}
            <Typography
                variant="body"
                color="inherit"
                weight={jnjFullBranded ? "inherit" : "600"}
                size={jnjFullBranded ? "m" : "s"}
                lineHeight={jnjFullBranded ? undefined : "l"}
                id={id ? `ToC.Link.${index}.${id}` : undefined}
                component="span"
            >
                {children}
            </Typography>
        </>
    );
};

export const TableOfContents = (props: PropTypes): JSX.Element => {
    const isBrowser = typeof document !== "undefined";
    const {
        sections,
        title,
        topPosition = "0",
        topMargin = 0,
        scrollArea = isBrowser ? document : null,
        topBorder = true,
        openStatus = (): void => null,
        closeLabel,
        openLabel,
    } = props;
    const { jnjFullBranded } = useJnjBranding();

    const scrollElement = ((scrollArea as Document)?.documentElement || scrollArea) as Element;

    const [isTableOfContentsClosed, setIsTableOfContentsClosed] = useState(true);
    const reference: MutableRefObject<HTMLDivElement> = useRef(null);
    const scrollerRef: MutableRefObject<HTMLUListElement> = useRef(null);
    const [activeItem, setActiveItem] = useState(0);
    const [activeTitle, setActiveTitle] = useState(-1);
    const [chevronIcon, setChevronIcon] = useState(null as chevronState);
    const isMobileToc = useMediaQuery(BreakPoint.md);
    const headerProps = useHeaderProperties();

    const tocStyles = {
        top: topPosition,
        maxHeight: isBrowser && isMobileToc ? `calc(100% - ${topPosition})` : "none",
    };

    // Chevron only shows up if the ToC has scroll
    const initializeChevron = (): void => {
        if (!chevronIcon && scrollerRef?.current?.clientHeight < scrollerRef?.current?.scrollHeight) {
            setChevronIcon("down");
        }
    };

    useEffect(() => {
        initializeChevron();
    }, [scrollerRef]);

    const onTocScroll = (): void => {
        scrollerRef?.current?.clientHeight + scrollerRef?.current?.scrollTop >=
        scrollerRef?.current?.scrollHeight - TOLERANCE
            ? setChevronIcon("up")
            : setChevronIcon("down");
    };

    useScroll(
        () => {
            let maxPercentage = 0;
            let itemIndex = -1;
            let titleIndex = -1;
            const tocHeight = document?.getElementById("toc-bar")?.offsetHeight || 0;

            sections?.forEach((section, index) => {
                const element = document.getElementById(section?.id);
                const title = element?.querySelector(`[data-id="sectionTitle"]`) || element?.querySelector("h2");
                if (element) {
                    // Look for the section with the highest percentage of visibility.
                    const percentage = getScreenPercentage(element, topMargin);
                    if (percentage > 0 && percentage >= maxPercentage && maxPercentage < 100) {
                        maxPercentage = percentage;
                        itemIndex = index;
                    }
                }
                if (title) {
                    // Look for section closer to the top of the screen.
                    const bounding = element?.getBoundingClientRect();
                    const headerHeight = headerProps?.isCollapsed || !isMobileToc ? 0 : headerProps?.height || 0;
                    const fullHeaderHeight = (index > 0 ? tocHeight : 0) + headerHeight + TOLERANCE;
                    const sectionOffset = bounding?.top - fullHeaderHeight;

                    if (sectionOffset < topMargin) {
                        titleIndex = index;
                    }
                }
            });

            const scrollAtBottom = scrollElement?.scrollTop
                ? scrollElement?.scrollTop + scrollElement?.clientHeight + TOLERANCE >= scrollElement?.scrollHeight
                : false;

            // If the scroll is at the bottom, the last section will always be active.
            if (scrollAtBottom) {
                itemIndex = sections?.length - 1;
            }

            if (itemIndex > -1 && itemIndex !== activeItem) {
                setActiveItem(itemIndex);
            }

            if (titleIndex !== activeTitle) {
                setActiveTitle(titleIndex);
            }

            if (itemIndex > -1) {
                const tocElement = document.getElementById(`TableOfContents.Item.${itemIndex}`);
                scrollerRef?.current?.scrollTo({
                    top: tocElement?.offsetTop - scrollerRef?.current?.clientHeight / 2 + tocElement?.clientHeight / 2,
                    behavior: "smooth",
                });
            }
        },
        SCROLL_DELAY,
        scrollArea,
        false,
    );

    const toggleOpen = (): void => {
        setIsTableOfContentsClosed(isTableOfContentsClosed ? false : true);
    };

    useEffect(() => {
        initializeChevron();
        openStatus(isTableOfContentsClosed);
    }, [isTableOfContentsClosed]);

    useEffect(() => {
        // Hides the component when clicking outside.
        const tableClickHandler: EventListener = (event: Event) => {
            if (reference.current && !reference.current.contains(event.target as Node)) {
                setIsTableOfContentsClosed(true);
            }
        };

        document.addEventListener("mouseup", tableClickHandler);
        return (): void => {
            // Unbind the event listener on clean up
            document.removeEventListener("mouseup", tableClickHandler);
        };
    }, [reference, isTableOfContentsClosed]);

    return (
        <div id="toc" className={style.toc} data-test-id="toc">
            <div
                id="toc-bar"
                className={classnames(
                    style.toggleBackground,
                    activeTitle > -1 ? style.visible : null,
                    topBorder ? style.topBorder : null,
                )}
            >
                <button
                    id="toc-toggle"
                    onClick={toggleOpen}
                    className={classnames(style.toggle, isTableOfContentsClosed ? style.closed : null)}
                    data-test-id="toc-toggle"
                    aria-label={openLabel}
                    title={openLabel}
                >
                    <div className={style.row}>
                        <div className={style.title}>
                            <Typography
                                variant="body"
                                color="dark"
                                weight={jnjFullBranded ? "500" : "600"}
                                size={jnjFullBranded ? "m" : "s"}
                                lineHeight="l"
                            >
                                {activeTitle > -1 ? sections?.[activeTitle]?.title : ""}
                            </Typography>
                        </div>
                        <div className={style.icon}>
                            {jnjFullBranded && isMobileToc ? (
                                <Icon icon={jnjChevronDown} type="jnj" color="text-dark" size="xs" />
                            ) : (
                                <Icon
                                    icon={mdiFormatListBulleted}
                                    size={jnjFullBranded ? "small" : "medium"}
                                    color="primary"
                                />
                            )}
                        </div>
                    </div>
                </button>
            </div>
            <div
                id="toc-content"
                ref={reference}
                className={classnames(style.content, isTableOfContentsClosed ? style.closed : null)}
                data-test-id="toc-content"
                style={tocStyles}
            >
                <div className={style.header}>
                    <div className={style.title}>
                        <Typography
                            variant={jnjFullBranded ? "body" : "h6"}
                            color="dark"
                            weight={jnjFullBranded ? "500" : "900"}
                            size={jnjFullBranded ? "m" : "inherit"}
                            lineHeight="l"
                        >
                            {jnjFullBranded ? (activeTitle > -1 ? sections?.[activeTitle]?.title : "") : title}
                        </Typography>
                    </div>
                    <button
                        className={style.close}
                        onClick={toggleOpen}
                        id="toc-close"
                        title={closeLabel}
                        aria-label={closeLabel}
                    >
                        {jnjFullBranded ? (
                            <Icon
                                icon={jnjChevronUp}
                                type="jnj"
                                color={isMobileToc ? "text-dark" : "primary"}
                                size={isMobileToc ? "xs" : "small"}
                            />
                        ) : (
                            <Icon
                                icon={mdiClose}
                                size="medium"
                                color="text-dark"
                                data-test-id="TableOfContents.close"
                            />
                        )}
                    </button>
                </div>
                <ul ref={scrollerRef} onScroll={onTocScroll}>
                    {sections?.map((section, index: number) => {
                        const anchor = "#" + section.id;

                        // if section has audience(s) applied
                        const additionalPropsRelatedToAudiences: {
                            "data-audiences"?: string;
                            "data-nosnippet"?: string;
                        } = {};
                        if (section.audienceIds) {
                            // add the data-audiences attribute with the ids of the audience(s)
                            additionalPropsRelatedToAudiences["data-audiences"] = section.audienceIds;
                        }
                        // if the audience(s) applied to the section are all hidden by default (in other words, none are shown by default) ...
                        if (section.shouldBeHiddenInToC) {
                            // ...add the data-nosnippet attribute
                            additionalPropsRelatedToAudiences["data-nosnippet"] = ""; // Any attribute with an empty string will render into the DOM without a value. (ref: https://www.w3.org/TR/html5/syntax.html#elements-attributes)
                        }

                        return (
                            <li
                                key={index}
                                className={classnames(
                                    style.link,
                                    index == activeItem ? style.active : null,
                                    section.shouldBeHiddenInToC ? style.hidden : null,
                                )}
                                id={`TableOfContents.Item.${index}`}
                                data-test-id={`TableOfContents.Item.${index}`}
                                onClick={toggleOpen}
                                {...additionalPropsRelatedToAudiences}
                                role="presentation"
                            >
                                {section.link ? (
                                    section.link
                                ) : (
                                    <a href={anchor}>
                                        <Item>{section.title}</Item>
                                    </a>
                                )}
                            </li>
                        );
                    })}
                </ul>
                {chevronIcon && !jnjFullBranded && (
                    <Icon
                        size="large"
                        className={classnames(style.chevron, chevronIcon === "up" && style.up)}
                        icon={mdiChevronDown}
                    />
                )}
            </div>
        </div>
    );
};

TableOfContents.Item = Item;

export default TableOfContents;
